import { Map as ImmutableMap, List as ImmutableList } from "immutable";

import api, { getLinks } from "flavours/glitch/api";
import { compareId } from "flavours/glitch/compare_id";
import { usePendingItems as preferPendingItems } from "flavours/glitch/initial_state";
import { toServerSideType } from "flavours/glitch/utils/filters";

import { importFetchedStatus, importFetchedStatuses } from "./importer";
import { submitMarkers, submitMarkersSuccess } from "./markers";

export const TIMELINE_UPDATE  = "TIMELINE_UPDATE";
export const TIMELINE_DELETE  = "TIMELINE_DELETE";
export const TIMELINE_CLEAR   = "TIMELINE_CLEAR";

export const TIMELINE_EXPAND_REQUEST = "TIMELINE_EXPAND_REQUEST";
export const TIMELINE_EXPAND_SUCCESS = "TIMELINE_EXPAND_SUCCESS";
export const TIMELINE_GAP_FILLED_UP = "TIMELINE_GAP_FILLED_UP";
export const TIMELINE_EXPAND_FAIL    = "TIMELINE_EXPAND_FAIL";

export const TIMELINE_SCROLL_TOP   = "TIMELINE_SCROLL_TOP";
export const TIMELINE_LOAD_PENDING = "TIMELINE_LOAD_PENDING";
export const TIMELINE_DISCONNECT   = "TIMELINE_DISCONNECT";
export const TIMELINE_CONNECT      = "TIMELINE_CONNECT";

export const TIMELINE_MARK_AS_PARTIAL = "TIMELINE_MARK_AS_PARTIAL";

export const TIMELINE_RESTORE_POSITION = "TIMELINE_RESTORE_POSITION";
export const TIMELINE_RESTORE_SCROLLED = "TIMELINE_RESTORE_SCROLLED";

//  Set by a client that reloads the page on purpose and wants the newest posts, e.g. pull
//  to refresh in the Android app. Read once.
const SKIP_RESTORE_KEY = "sloth.skipRestorePosition";

export const loadPending = timeline => ({
  type: TIMELINE_LOAD_PENDING,
  timeline,
});

export function updateTimeline(timeline, status, accept) {
  return (dispatch, getState) => {
    if (typeof accept === "function" && !accept(status)) {
      return;
    }

    if (getState().getIn(["timelines", timeline, "isPartial"])) {
      // Prevent new items from being added to a partial timeline,
      // since it will be reloaded anyway

      return;
    }

    let filtered = false;

    if (status.filtered) {
      const contextType = toServerSideType(timeline);
      const filters = status.filtered.filter(result => result.filter.context.includes(contextType));

      filtered = filters.length > 0;
    }

    dispatch(importFetchedStatus(status));

    dispatch({
      type: TIMELINE_UPDATE,
      timeline,
      status,
      usePendingItems: preferPendingItems,
      filtered,
    });

    if (timeline === "home") {
      dispatch(submitMarkers());
    }
  };
}

export function deleteFromTimelines(id) {
  return (dispatch, getState) => {
    const accountId  = getState().getIn(["statuses", id, "account"]);
    const references = getState().get("statuses").filter(status => status.get("reblog") === id).map(status => status.get("id"));
    const reblogOf   = getState().getIn(["statuses", id, "reblog"], null);

    dispatch({
      type: TIMELINE_DELETE,
      id,
      accountId,
      references,
      reblogOf,
    });
  };
}

export function clearTimeline(timeline) {
  return (dispatch) => {
    dispatch({ type: TIMELINE_CLEAR, timeline });
  };
}

const noOp = () => {};

const parseTags = (tags = {}, mode) => {
  return (tags[mode] || []).map((tag) => {
    return tag.value;
  });
};

// Path and filter parameters of the last request per timeline, so a gap can be
// filled with the same query (fillTimelineGapUpward).
const timelineRequests = new Map();

export function expandTimeline(timelineId, path, params = {}, done = noOp) {
  return (dispatch, getState) => {
    if (!params.pinned) {
      const { max_id, since_id, min_id, ...query } = params;
      timelineRequests.set(timelineId, { path, query });
    }

    const timeline = getState().getIn(["timelines", timelineId], ImmutableMap());
    const isLoadingMore = !!params.max_id;

    if (timeline.get("isLoading")) {
      done();
      return;
    }

    if (!params.max_id && !params.pinned && (timeline.get("items", ImmutableList()).size + timeline.get("pendingItems", ImmutableList()).size) > 0) {
      const a = timeline.getIn(["pendingItems", 0]);
      const b = timeline.getIn(["items", 0]);

      if (a && b && compareId(a, b) > 0) {
        params.since_id = a;
      } else {
        params.since_id = b || a;
      }
    }

    const isLoadingRecent = !!params.since_id;

    dispatch(expandTimelineRequest(timelineId, isLoadingMore));

    api(getState).get(path, { params }).then(response => {
      const next = getLinks(response).refs.find(link => link.rel === "next");
      dispatch(importFetchedStatuses(response.data));
      dispatch(expandTimelineSuccess(timelineId, response.data, next ? next.uri : null, response.status === 206, isLoadingRecent, isLoadingMore, isLoadingRecent && preferPendingItems));

      if (timelineId === "home") {
        dispatch(submitMarkers());
      }
    }).catch(error => {
      dispatch(expandTimelineFail(timelineId, error, isLoadingMore));
    }).finally(() => {
      done();
    });
  };
}

const GAP_PAGE_SIZE = 20;

// Fills a gap from its lower end: loads the posts immediately newer than the post
// below the gap (min_id), so reading can go on upwards from there without a jump.
// The gap stays above the loaded posts until the post above it is reached.
// Returns false when the timeline cannot be filled this way (no known request),
// so the caller can load from the upper end as before.
export const fillTimelineGapUpward = (timelineId, belowId) => (dispatch, getState) => {
  const request = timelineRequests.get(timelineId);
  const timeline = getState().getIn(["timelines", timelineId]);

  if (!request || !timeline || !belowId) {
    return false;
  }

  if (timeline.get("isLoading")) {
    return true;
  }

  const items = timeline.get("items");
  const gapIndex = items.take(items.indexOf(belowId)).lastIndexOf(null);

  if (gapIndex < 0) {
    return false;
  }

  const lowerId = items.get(gapIndex + 1);
  const upperId = gapIndex > 0 ? items.get(gapIndex - 1) : null;

  dispatch(expandTimelineRequest(timelineId, true));

  api(getState).get(request.path, { params: { ...request.query, min_id: lowerId, limit: GAP_PAGE_SIZE } }).then(response => {
    const statuses = [...response.data].sort((a, b) => compareId(b.id, a.id));
    const closed = statuses.length === 0
      || (upperId ? statuses.some(status => compareId(status.id, upperId) >= 0) : statuses.length < GAP_PAGE_SIZE);

    dispatch(importFetchedStatuses(statuses));
    dispatch({ type: TIMELINE_GAP_FILLED_UP, timeline: timelineId, statuses, lowerId, upperId, closed });
  }).catch(error => {
    dispatch(expandTimelineFail(timelineId, error, true));
  });

  return true;
};

export function fillTimelineGaps(timelineId, path, params = {}, done = noOp) {
  return (dispatch, getState) => {
    const timeline = getState().getIn(["timelines", timelineId], ImmutableMap());
    const items = timeline.get("items");
    const nullIndexes = items.map((statusId, index) => statusId === null ? index : null);
    const gaps = nullIndexes.map(index => index > 0 ? items.get(index - 1) : null);

    // Only expand at most two gaps to avoid doing too many requests
    done = gaps.take(2).reduce((done, maxId) => {
      return (() => dispatch(expandTimeline(timelineId, path, { ...params, maxId }, done)));
    }, done);

    done();
  };
}

export const expandHomeTimeline            = ({ maxId } = {}, done = noOp) => expandTimeline("home", "/api/v1/timelines/home", { max_id: maxId }, done);
export const expandPublicTimeline          = ({ maxId, onlyMedia, onlyRemote, allowLocalOnly } = {}, done = noOp) => expandTimeline(`public${onlyRemote ? ":remote" : (allowLocalOnly ? ":allow_local_only" : "")}${onlyMedia ? ":media" : ""}`, "/api/v1/timelines/public", { remote: !!onlyRemote, allow_local_only: !!allowLocalOnly, max_id: maxId, only_media: !!onlyMedia }, done);
export const expandCommunityTimeline       = ({ maxId, onlyMedia } = {}, done = noOp) => expandTimeline(`community${onlyMedia ? ":media" : ""}`, "/api/v1/timelines/public", { local: true, max_id: maxId, only_media: !!onlyMedia }, done);
export const expandDirectTimeline          = ({ maxId } = {}, done = noOp) => expandTimeline("direct", "/api/v1/timelines/direct", { max_id: maxId }, done);
export const expandAccountTimeline         = (accountId, { maxId, withReplies, tagged } = {}) => expandTimeline(`account:${accountId}${withReplies ? ":with_replies" : ""}${tagged ? `:${tagged}` : ""}`, `/api/v1/accounts/${accountId}/statuses`, { exclude_replies: !withReplies, exclude_reblogs: withReplies, tagged, max_id: maxId });
export const expandAccountFeaturedTimeline = (accountId, { tagged } = {}) => expandTimeline(`account:${accountId}:pinned`, `/api/v1/accounts/${accountId}/statuses`, { pinned: true, tagged });
export const expandAccountMediaTimeline    = (accountId, { maxId } = {}) => expandTimeline(`account:${accountId}:media`, `/api/v1/accounts/${accountId}/statuses`, { max_id: maxId, only_media: true, limit: 40 });
export const expandListTimeline            = (id, { maxId } = {}, done = noOp) => expandTimeline(`list:${id}`, `/api/v1/timelines/list/${id}`, { max_id: maxId }, done);
export const expandHashtagTimeline         = (hashtag, { maxId, tags, local } = {}, done = noOp) => {
  return expandTimeline(`hashtag:${hashtag}${local ? ":local" : ""}`, `/api/v1/timelines/tag/${hashtag}`, {
    max_id: maxId,
    any:    parseTags(tags, "any"),
    all:    parseTags(tags, "all"),
    none:   parseTags(tags, "none"),
    local:  local,
  }, done);
};

//  Continue the home timeline where the user stopped reading (settings.home.restorePosition).
//  The position is the home marker. If that post is among the posts loaded at start, the
//  column only scrolls there. Otherwise the post and the page below it are loaded and put
//  after the newest posts, separated by a gap. "Load more" fills that gap upwards from the
//  restored post (fillTimelineGapUpward), so reading can continue from there.
//  Runs after the first page of the home timeline has loaded.
export function restoreHomeReadingPosition() {
  return async (dispatch, getState) => {
    if (!getState().getIn(["settings", "home", "restorePosition"], false)) {
      return;
    }

    try {
      if (sessionStorage.getItem(SKIP_RESTORE_KEY)) {
        sessionStorage.removeItem(SKIP_RESTORE_KEY);
        return;
      }
    } catch (e) {
      // sessionStorage unavailable
    }

    let markerId;

    try {
      const response = await api(getState).get("/api/v1/markers", { params: { timeline: ["home"] } });
      markerId = response.data?.home?.last_read_id;
    } catch (e) {
      return;
    }

    if (!markerId) {
      return;
    }

    // Known as submitted, so an unchanged position is not sent again
    dispatch(submitMarkersSuccess({ home: { last_read_id: markerId } }));

    const items = getState().getIn(["timelines", "home", "items"], ImmutableList());

    if (items.includes(markerId)) {
      dispatch({ type: TIMELINE_RESTORE_POSITION, timeline: "home", ids: [], scrollTo: markerId });
      return;
    }

    const [anchorResult, olderResult] = await Promise.allSettled([
      api(getState).get(`/api/v1/statuses/${markerId}`),
      api(getState).get("/api/v1/timelines/home", { params: { max_id: markerId } }),
    ]);

    const anchor = anchorResult.status === "fulfilled" ? anchorResult.value.data : null;
    const older  = olderResult.status === "fulfilled" ? olderResult.value.data : [];

    if (!anchor && older.length === 0) {
      return;
    }

    if (anchor) {
      dispatch(importFetchedStatus(anchor));
    }
    dispatch(importFetchedStatuses(older));

    const ids = (anchor ? [anchor] : []).concat(older).map(status => status.id);

    dispatch({
      type: TIMELINE_RESTORE_POSITION,
      timeline: "home",
      ids,
      hasMore: older.length > 0,
      scrollTo: ids[0],
    });
  };
}

export const restoreScrolled = (timeline) => ({
  type: TIMELINE_RESTORE_SCROLLED,
  timeline,
});

export const fillHomeTimelineGaps      = (done = noOp) => fillTimelineGaps("home", "/api/v1/timelines/home", {}, done);
export const fillPublicTimelineGaps    = ({ onlyMedia, onlyRemote, allowLocalOnly } = {}, done = noOp) => fillTimelineGaps(`public${onlyRemote ? ":remote" : (allowLocalOnly ? ":allow_local_only" : "")}${onlyMedia ? ":media" : ""}`, "/api/v1/timelines/public", { remote: !!onlyRemote, only_media: !!onlyMedia, allow_local_only: !!allowLocalOnly }, done);
export const fillCommunityTimelineGaps = ({ onlyMedia } = {}, done = noOp) => fillTimelineGaps(`community${onlyMedia ? ":media" : ""}`, "/api/v1/timelines/public", { local: true, only_media: !!onlyMedia }, done);
export const fillListTimelineGaps      = (id, done = noOp) => fillTimelineGaps(`list:${id}`, `/api/v1/timelines/list/${id}`, {}, done);

export function expandTimelineRequest(timeline, isLoadingMore) {
  return {
    type: TIMELINE_EXPAND_REQUEST,
    timeline,
    skipLoading: !isLoadingMore,
  };
}

export function expandTimelineSuccess(timeline, statuses, next, partial, isLoadingRecent, isLoadingMore, usePendingItems) {
  return {
    type: TIMELINE_EXPAND_SUCCESS,
    timeline,
    statuses,
    next,
    partial,
    isLoadingRecent,
    usePendingItems,
    skipLoading: !isLoadingMore,
  };
}

export function expandTimelineFail(timeline, error, isLoadingMore) {
  return {
    type: TIMELINE_EXPAND_FAIL,
    timeline,
    error,
    skipLoading: !isLoadingMore,
    skipNotFound: timeline.startsWith("account:"),
  };
}

export function scrollTopTimeline(timeline, top) {
  return {
    type: TIMELINE_SCROLL_TOP,
    timeline,
    top,
  };
}

export function connectTimeline(timeline) {
  return {
    type: TIMELINE_CONNECT,
    timeline,
    usePendingItems: preferPendingItems,
  };
}

export const disconnectTimeline = timeline => ({
  type: TIMELINE_DISCONNECT,
  timeline,
  usePendingItems: preferPendingItems,
});

export const markAsPartial = timeline => ({
  type: TIMELINE_MARK_AS_PARTIAL,
  timeline,
});
